/**
 * Accessibility Utilities
 *
 * Provides utilities for keyboard navigation, focus management, and accessibility features
 * across all breakpoints and device types.
 */

/**
 * Get all focusable elements within a container
 */
export const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  const selector = [
    'a[href]:not([disabled])',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ].join(', ')

  return Array.from(container.querySelectorAll<HTMLElement>(selector))
}

/**
 * Trap focus within a container (for modals, drawers, etc.)
 */
export const trapFocus = (
  container: HTMLElement,
  event: KeyboardEvent
): void => {
  if (event.key !== 'Tab') return

  const focusableElements = getFocusableElements(container)
  if (focusableElements.length === 0) return

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]

  if (event.shiftKey) {
    // Shift + Tab: moving backwards
    if (document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    }
  } else {
    // Tab: moving forwards
    if (document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }
}

/**
 * Focus the first focusable element in a container
 */
export const focusFirstElement = (container: HTMLElement): void => {
  const focusableElements = getFocusableElements(container)
  if (focusableElements.length > 0) {
    focusableElements[0].focus()
  }
}

/**
 * Focus the last focusable element in a container
 */
export const focusLastElement = (container: HTMLElement): void => {
  const focusableElements = getFocusableElements(container)
  if (focusableElements.length > 0) {
    focusableElements[focusableElements.length - 1].focus()
  }
}

/**
 * Restore focus to a previously focused element
 */
export const restoreFocus = (element: HTMLElement | null): void => {
  if (element && document.contains(element)) {
    element.focus()
  }
}

/**
 * Check if an element is currently visible and focusable
 */
export const isElementFocusable = (element: HTMLElement): boolean => {
  // Check if element is visible
  const style = window.getComputedStyle(element)
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0'
  ) {
    return false
  }

  // Check if element is disabled
  if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') {
    return false
  }

  // Check if element has negative tabindex
  const tabindex = element.getAttribute('tabindex')
  if (tabindex === '-1') {
    return false
  }

  return true
}

/**
 * Get the next focusable element in the tab order
 */
export const getNextFocusableElement = (
  currentElement: HTMLElement,
  container?: HTMLElement
): HTMLElement | null => {
  const root = container || document.body
  const focusableElements = getFocusableElements(root)
  const currentIndex = focusableElements.indexOf(currentElement)

  if (currentIndex === -1 || currentIndex === focusableElements.length - 1) {
    return null
  }

  return focusableElements[currentIndex + 1]
}

/**
 * Get the previous focusable element in the tab order
 */
export const getPreviousFocusableElement = (
  currentElement: HTMLElement,
  container?: HTMLElement
): HTMLElement | null => {
  const root = container || document.body
  const focusableElements = getFocusableElements(root)
  const currentIndex = focusableElements.indexOf(currentElement)

  if (currentIndex <= 0) {
    return null
  }

  return focusableElements[currentIndex - 1]
}

/**
 * Create a focus trap hook for React components
 */
export const createFocusTrap = (
  containerRef: { current: HTMLElement | null },
  isActive: boolean,
  options?: {
    initialFocus?: 'first' | 'last' | HTMLElement
    returnFocus?: boolean
  }
) => {
  const previouslyFocusedElement = { current: null as HTMLElement | null }

  const useEffectCallback = () => {
    if (!isActive || !containerRef.current) return

    const container = containerRef.current

    // Save previously focused element
    if (options?.returnFocus) {
      previouslyFocusedElement.current = document.activeElement as HTMLElement
    }

    // Set initial focus
    if (options?.initialFocus === 'first') {
      focusFirstElement(container)
    } else if (options?.initialFocus === 'last') {
      focusLastElement(container)
    } else if (options?.initialFocus instanceof HTMLElement) {
      options.initialFocus.focus()
    }

    // Handle keyboard events
    const handleKeyDown = (event: KeyboardEvent) => {
      trapFocus(container, event)
    }

    container.addEventListener('keydown', handleKeyDown)

    return () => {
      container.removeEventListener('keydown', handleKeyDown)

      // Restore focus when trap is deactivated
      if (options?.returnFocus && previouslyFocusedElement.current) {
        restoreFocus(previouslyFocusedElement.current)
      }
    }
  }

  return useEffectCallback
}

/**
 * Announce message to screen readers using ARIA live region
 */
export const announceToScreenReader = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void => {
  // Find or create live region
  let liveRegion = document.getElementById('aria-live-region')

  if (!liveRegion) {
    liveRegion = document.createElement('div')
    liveRegion.id = 'aria-live-region'
    liveRegion.setAttribute('aria-live', priority)
    liveRegion.setAttribute('aria-atomic', 'true')
    liveRegion.className = 'sr-only'
    document.body.appendChild(liveRegion)
  }

  // Update priority if needed
  liveRegion.setAttribute('aria-live', priority)

  // Clear and set new message
  liveRegion.textContent = ''
  setTimeout(() => {
    liveRegion!.textContent = message
  }, 100)
}

/**
 * Skip to main content functionality
 */
export const skipToMainContent = (): void => {
  const mainContent = document.querySelector('main') || document.getElementById('main-content')
  if (mainContent) {
    mainContent.setAttribute('tabindex', '-1')
    ;(mainContent as HTMLElement).focus()
    mainContent.removeAttribute('tabindex')
  }
}

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Check if user is using keyboard navigation
 */
export const isUsingKeyboard = (): boolean => {
  return document.body.classList.contains('using-keyboard')
}

/**
 * Initialize keyboard navigation detection
 */
export const initKeyboardNavigation = (): (() => void) => {
  let isKeyboard = false

  const handleMouseDown = () => {
    if (isKeyboard) {
      isKeyboard = false
      document.body.classList.remove('using-keyboard')
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Tab' && !isKeyboard) {
      isKeyboard = true
      document.body.classList.add('using-keyboard')
    }
  }

  document.addEventListener('mousedown', handleMouseDown)
  document.addEventListener('keydown', handleKeyDown)

  return () => {
    document.removeEventListener('mousedown', handleMouseDown)
    document.removeEventListener('keydown', handleKeyDown)
  }
}
